/**
 * VariantTimeline — a workflow's variants as a DataTable (UI-SPEC §3.4): version,
 * state, created_by, rationale, eval delta vs incumbent (signed/colored), and
 * created_at. Row actions (promote/rollback/diff) are injected by the page as a
 * client island; this block is presentational.
 */

import * as React from "react";
import { DataTable, type DataTableColumn } from "../data-table";
import { StatusBadge } from "../status-badge";
import { formatDateTime } from "../../format";
import type { LoopVariantStatus } from "../../tokens";

export interface VariantTimelineItem {
  id: number;
  version: number;
  state: LoopVariantStatus;
  created_by: string;
  rationale: string | null;
  eval_summary: string | null; // JSON {score, verdict}
  created_at: string;
}

function deltaCell(summary: string | null): React.ReactNode {
  if (!summary) return <span className="text-muted-foreground">—</span>;
  try {
    const parsed = JSON.parse(summary);
    const v = parsed.verdict;
    if (!v) return <span className="text-muted-foreground">—</span>;
    const tok = v.prompt_token_delta_pct;
    const held = v.floors_held;
    return (
      <span className="tabular-nums">
        {typeof tok === "number" ? (
          <span className={tok < 0 ? "text-success" : tok > 0 ? "text-destructive" : ""}>
            {tok > 0 ? "+" : ""}
            {tok.toFixed(0)}% tok
          </span>
        ) : null}
        {held === false ? <span className="text-destructive"> · floors✗</span> : null}
      </span>
    );
  } catch {
    return <span className="text-muted-foreground">—</span>;
  }
}

export function VariantTimeline({
  variants,
  rowActions,
}: {
  variants: VariantTimelineItem[];
  /** Per-row action buttons (client island) keyed by variant id. */
  rowActions?: (v: VariantTimelineItem) => React.ReactNode;
}) {
  const columns: DataTableColumn<VariantTimelineItem>[] = [
    { key: "version", header: "version", cellClassName: "tabular-nums", render: (v) => `v${v.version}` },
    { key: "state", header: "state", align: "center", render: (v) => <StatusBadge domain="loop_variant" status={v.state} /> },
    { key: "by", header: "created by", cellClassName: "font-mono type-subtext text-muted-foreground", render: (v) => v.created_by },
    { key: "rationale", header: "rationale", render: (v) => v.rationale ?? "—" },
    { key: "delta", header: "eval Δ", render: (v) => deltaCell(v.eval_summary) },
    { key: "created", header: "created", cellClassName: "type-subtext text-muted-foreground", render: (v) => formatDateTime(v.created_at) },
  ];
  if (rowActions) {
    columns.push({ key: "actions", header: "", render: (v) => rowActions(v) });
  }
  return <DataTable rows={variants} columns={columns} rowKey={(v) => v.id} empty="No variants yet." />;
}
